/*
  Product page: switch colours IN PLACE (master list #60, 29 Sep 2026).
  Scope and reasoning: Shopify Editor/colour-swap-290926/SCOPE.md.

  Each colour of a style is its own product, and the COLOUR swatches (Custom Liquid
  block, a.swatch-item) are plain links to it. Clarity showed the swatches are the
  most-tapped control on product pages and a main source of "quick backs", so a tap
  now swaps the product area (everything in <main>) without a page load:

  - Loading starts on touch / hover, and pages are kept in memory (revisits are instant).
  - The address bar shows the new colour (pushState); back/forward switch in place.
  - The chosen size carries over (assets/default-size.js, LPDefaultSize.apply).
  - The Fera stars are carried over: Fera groups a style's colours (same rating).
  - Recently Viewed is refreshed from the new page (it records the new colour).
  - Product views are sent by hand to GA4/Google Ads, the Meta pixel and Klaviyo.
    Shopify's own "product viewed" can't be sent by a theme (scope trade-off).
  - Anything unexpected (slow > 6s, error, a colour on a different product template)
    falls back to a normal page load, exactly as before.
*/
(() => {
  if (window.LPColourSwitch) return;
  const script = document.currentScript;
  const sectionId = script && script.dataset.section;
  const main = document.getElementById('MainContent') || document.querySelector('main');
  if (!sectionId || !main || !window.fetch || !window.history.pushState) return;

  const SWATCH = `#ProductInfo-${sectionId} a.swatch-item`;
  const TIMEOUT = 6000;
  const cache = new Map();
  const loadedSrcs = new Set(Array.from(document.scripts, (s) => s.src).filter(Boolean));
  let currentPath = window.location.pathname;
  let seq = 0;

  const style = document.createElement('style');
  style.textContent =
    'main.lp-colour-loading > .shopify-section{opacity:.55;transition:opacity .15s ease}' +
    'main.lp-colour-loading{cursor:progress}';
  document.head.appendChild(style);

  const toUrl = (href) => {
    const url = new URL(href, window.location.origin);
    url.searchParams.delete('size');
    url.searchParams.delete('variant');
    return url;
  };

  // #107: alternate layouts (e.g. the Meta ad page, ?view=meta) stay in that layout when switching colour.
  const view = new URLSearchParams(window.location.search).get('view');
  const withView = (path) => (view ? `${path}?view=${encodeURIComponent(view)}` : path);

  // Fetch + parse a colour's page once; failures aren't cached, so they can retry.
  const load = (path) => {
    if (!cache.has(path)) {
      const controller = 'AbortController' in window ? new AbortController() : null;
      const timer = setTimeout(() => controller && controller.abort(), TIMEOUT);
      const promise = fetch(withView(path), { credentials: 'same-origin', signal: controller && controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((text) => new DOMParser().parseFromString(text, 'text/html'))
        .catch(() => null)
        .finally(() => clearTimeout(timer));
      cache.set(path, promise);
      promise.then((doc) => {
        if (!doc) cache.delete(path);
      });
    }
    return cache.get(path);
  };

  const readMeta = (doc) => {
    for (const s of doc.querySelectorAll('script:not([src])')) {
      const match = s.textContent.match(/var meta = (\{[\s\S]*?\});\s*\n/);
      if (match) {
        try {
          return JSON.parse(match[1]);
        } catch (e) {
          return null;
        }
      }
    }
    return null;
  };

  const updateHead = (doc) => {
    document.title = doc.title;
    const selectors = [
      'meta[name="description"]',
      'link[rel="canonical"]',
      'meta[property^="og:"]',
      'meta[name^="twitter:"]',
    ];
    selectors.forEach((selector) => {
      document.head.querySelectorAll(selector).forEach((el) => el.remove());
      doc.head.querySelectorAll(selector).forEach((el) => document.head.appendChild(el.cloneNode(true)));
    });
  };

  // Scripts that wait for DOMContentLoaded / load (e.g. SS Product reviews moving itself under
  // ADD TO BAG and starting its slider) would never run after a switch: the page loaded long ago.
  // While re-running the swapped-in scripts, such listeners are called straight away instead.
  const runLoadListenersNow = (target) => {
    const original = target.addEventListener;
    target.addEventListener = function (type, listener, options) {
      if ((type === 'DOMContentLoaded' || type === 'load') && listener) {
        setTimeout(() => {
          try {
            if (typeof listener === 'function') listener.call(target, new Event(type));
            else if (listener.handleEvent) listener.handleEvent(new Event(type));
          } catch (e) {
            console.error(e);
          }
        }, 0);
        return;
      }
      return original.call(this, type, listener, options);
    };
    return () => {
      target.addEventListener = original;
    };
  };

  // innerHTML doesn't run scripts: run the new inline ones, load any script not loaded yet.
  const runScripts = (root) => {
    const restoreWindow = runLoadListenersNow(window);
    const restoreDocument = runLoadListenersNow(document);
    try {
      runScriptsNow(root);
    } finally {
      restoreWindow();
      restoreDocument();
    }
  };

  const runScriptsNow = (root) => {
    root.querySelectorAll('script').forEach((old) => {
      const type = (old.getAttribute('type') || '').toLowerCase();
      if (type && type !== 'text/javascript' && type !== 'module' && type !== 'application/javascript') return;
      if (old.src) {
        if (loadedSrcs.has(old.src)) return;
        loadedSrcs.add(old.src);
      } else {
        const code = old.textContent;
        // The Fera positioning script is already watching the page (one instance only).
        if (code.includes('fera-productDetailRating-widget')) return;
        // Web components already defined upgrade by themselves; re-defining would throw.
        const defined = code.match(/customElements\.define\(\s*['"]([\w-]+)['"]/);
        if (defined && customElements.get(defined[1])) return;
      }
      const fresh = document.createElement('script');
      Array.from(old.attributes).forEach((attr) => fresh.setAttribute(attr.name, attr.value));
      if (!old.src) fresh.textContent = old.textContent;
      old.replaceWith(fresh);
    });
  };

  const refreshRecentlyViewed = (doc) => {
    const oldEl = document.querySelector('recently-viewed');
    const newEl = doc.querySelector('recently-viewed');
    const oldSection = oldEl && oldEl.closest('.shopify-section');
    const newSection = newEl && newEl.closest('.shopify-section');
    if (oldSection && newSection) oldSection.innerHTML = newSection.innerHTML;
  };

  const trackView = (meta) => {
    const product = meta && meta.product;
    if (!product) return;
    const variant = (product.variants || [])[0] || {};
    const price = (variant.price || 0) / 100;
    const currency = meta.currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AUD';
    const title = document.title;
    const image = (document.head.querySelector('meta[property="og:image"]') || {}).content || '';
    const url = window.location.origin + currentPath;
    // Phone tap speed (26 Oct 2026): the three trackers took ~0.7s of one block on a mid-range phone right after the
    // switch, so a size tap straight after a colour switch waited for them. Each now runs as its own small task after
    // the new colour has painted, so taps in between are answered first. Same events, a fraction of a second later.
    const tasks = [];
    tasks.push(() => { try {
      if (typeof window.gtag === 'function') {
        gtag('event', 'page_view', { page_location: url, page_title: title });
        gtag('event', 'view_item', {
          currency,
          value: price,
          items: [{ item_id: String(product.id), item_name: variant.name || title, item_brand: product.vendor, item_category: product.type, price }],
        });
      }
    } catch (e) {} });
    tasks.push(() => { try {
      if (typeof window.fbq === 'function') {
        fbq('track', 'ViewContent', {
          content_ids: [String(product.id)],
          content_type: 'product_group',
          content_name: title,
          value: price,
          currency,
        });
      }
    } catch (e) {} });
    tasks.push(() => { try {
      if (window.klaviyo && typeof klaviyo.track === 'function') {
        // Same shape as Klaviyo's own product-page snippet (Name, Price "$329", Value, CompareAtPrice), so Browse
        // Abandonment emails ({{ event.Name }}, {{ event.Price }}) work after an in-place colour switch too (19 Oct 2026).
        // Product title = variant name without its size (the page <title> is the longer SEO title).
        const vt = variant.public_title || '';
        const productTitle = vt && (variant.name || '').endsWith(' - ' + vt) ? variant.name.slice(0, -(vt.length + 3)) : (variant.name || title);
        let money = String(price);
        try { money = new Intl.NumberFormat('en-AU', { style: 'currency', currency, minimumFractionDigits: price % 1 ? 2 : 0, maximumFractionDigits: 2 }).format(price); } catch (e) {}
        const item = {
          Name: productTitle,
          ProductName: productTitle,
          ProductID: String(product.id),
          SKU: variant.sku || '',
          Categories: product.type ? [product.type] : [],
          ImageURL: image.indexOf('http://') === 0 ? 'https://' + image.slice(7) : image,
          URL: url,
          Brand: product.vendor,
          Price: money,
          Value: String(price),
        };
        klaviyo.track('Viewed Product', item);
        if (typeof klaviyo.trackViewedItem === 'function') {
          klaviyo.trackViewedItem({
            Title: item.ProductName,
            ItemId: item.ProductID,
            Categories: item.Categories,
            ImageUrl: item.ImageURL,
            Url: item.URL,
            Metadata: { Brand: item.Brand, Price: item.Price },
          });
        }
      }
    } catch (e) {} });
    const next = () => {
      const task = tasks.shift();
      if (!task) return;
      task();
      if (tasks.length) setTimeout(next, 0);
    };
    requestAnimationFrame(() => setTimeout(next, 0));
  };

  const setSelectedSwatch = (path) => {
    document.querySelectorAll(SWATCH).forEach((link) => {
      link.classList.toggle('swatch-item--selected', toUrl(link.href).pathname === path);
    });
  };

  const switchTo = async (href, push) => {
    const url = toUrl(href);
    const path = url.pathname;
    if (path === currentPath) return;
    const token = ++seq;
    const carriedSize = window.LPDefaultSize ? window.LPDefaultSize.chosenSize() : null;

    setSelectedSwatch(path);
    // #85: let the selected ring paint before the heavy swap, so the tap feels instant
    // (on slow phones the swap could hold the screen for ~0.4s with no visible change).
    await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
    if (token !== seq) return;
    const dim = setTimeout(() => main.classList.add('lp-colour-loading'), 150);
    const doc = await load(path);
    clearTimeout(dim);
    if (token !== seq) return; // a later tap won
    main.classList.remove('lp-colour-loading');

    const newMain = doc && (doc.getElementById('MainContent') || doc.querySelector('main'));
    // Same product template only (section ids match); otherwise a normal page load.
    if (!newMain || !newMain.querySelector(`#shopify-section-${sectionId}`)) {
      window.location.assign(url.href);
      return;
    }

    const scrollY = window.scrollY;
    const feraStars = main.querySelector('.fera-rating--inline-price');
    if (feraStars) feraStars.remove();
    const oldStickyBars = Array.from(main.querySelectorAll('section-store--sticky-atc-block'));

    // Photo zoom windows: Dawn moves each one from the product section to the end of <body> when the
    // page loads, so the swap below doesn't replace it and every colour switch left the old colour's
    // window behind (5 copies after 4 switches; Clarity "activeMedia" errors). Remove the old ones;
    // the new colour's window moves itself into place as before (adam, 12 Oct 2026).
    document.querySelectorAll('product-modal').forEach((m) => { if (!main.contains(m)) m.remove(); });

    main.innerHTML = newMain.innerHTML;

    // Section Store's sticky Add to Bag: a removed bar's scroll watcher lives on, finds the
    // new ADD TO BAG button and then fails on its own cleared wrapper ("null classList").
    // Give it harmless stand-ins; the new bar in the swapped HTML works normally.
    oldStickyBars.forEach((bar) => {
      bar.wrapper = document.createElement('div');
      bar.box = document.createElement('div');
    });
    currentPath = path;
    if (push) window.history.pushState({ lpColour: path }, '', withView(path));

    if (feraStars) {
      const priceContainer = main.querySelector('.product__info-container .price__container');
      if (priceContainer) priceContainer.insertAdjacentElement('afterend', feraStars);
    }
    updateHead(doc);
    runScripts(main);
    refreshRecentlyViewed(doc);

    const meta = readMeta(doc);
    if (meta && window.ShopifyAnalytics) window.ShopifyAnalytics.meta = Object.assign({}, window.ShopifyAnalytics.meta, meta);

    window.scrollTo(0, scrollY);
    if (window.LPDefaultSize) window.LPDefaultSize.apply(new URLSearchParams(''), carriedSize);
    document.dispatchEvent(new CustomEvent('lp:colour-switched', { detail: { path } }));
    trackView(meta);
  };

  // Start loading on touch / hover so the switch is (near) instant on tap.
  const prefetch = (event) => {
    const link = event.target.closest && event.target.closest(SWATCH);
    if (link) load(toUrl(link.href).pathname);
  };
  document.addEventListener('pointerdown', prefetch, { passive: true });
  document.addEventListener('mouseover', prefetch, { passive: true });

  document.addEventListener('click', (event) => {
    const link = event.target.closest(SWATCH);
    if (!link || event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    // Tapping the colour already shown does nothing (it used to reload the page).
    if (toUrl(link.href).pathname === currentPath) return;
    switchTo(link.href, true);
  });

  // Back / forward between colours: switch in place (Dawn's size change replaces the
  // history state with {}, so go by the path, not the state).
  window.history.replaceState(Object.assign({}, window.history.state, { lpColour: currentPath }), '');
  window.addEventListener('popstate', () => {
    if (window.location.pathname !== currentPath) switchTo(window.location.href, false);
  });

  window.LPColourSwitch = { switchTo };
})();
